import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import api from "../../../utils/axios";
import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import {
  getAdminMarketplaceProduct,
  updateAdminMarketplaceProduct,
} from "../../../features/marketplace/adminMarketplaceApi";
import {
  CATEGORY_LABELS,
  LISTING_STATUSES,
  PRODUCT_CATEGORIES,
  PRODUCT_SUBTYPES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import cls from "./AdminEditProductPage.module.scss";

const IMAGE_LIMIT = 6;
const VIDEO_LIMIT = 2;
let mediaItemSeq = 0;

const EMPTY_FORM = {
  category: PRODUCT_CATEGORIES.PHYSICAL,
  serviceType: "",
  digitalType: "",
  status: LISTING_STATUSES.DRAFT,
  title: "",
  description: "",
  priceNoty: "",
  listingImageKeys: [],
  listingVideoIds: [],
  deliveryMethod: "",
  deliveryDescription: "",
  location: "",
  locationDescription: "",
  onlineMethod: "",
  methodDescription: "",
  digitalImagePackKeys: [],
  digitalVideoId: "",
  digitalPdfKey: "",
  hasCommissionOverride: false,
  commissionPlatformPercent: "",
  commissionPartnerPercent: "",
};

const makeMediaItemId = (prefix) => `${prefix}-${Date.now()}-${mediaItemSeq++}`;

const extFromFile = (file) => {
  const name = String(file?.name || "");
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
};

async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        URL.revokeObjectURL(el.src || "");
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const mapProductToForm = (product) => ({
  category: product?.category || PRODUCT_CATEGORIES.PHYSICAL,
  serviceType: product?.serviceType || "",
  digitalType: product?.digitalType || "",
  status: product?.status || LISTING_STATUSES.DRAFT,
  title: product?.title || "",
  description: product?.description || "",
  priceNoty: String(product?.priceNoty ?? ""),
  listingImageKeys: Array.isArray(product?.listingImageKeys)
    ? product.listingImageKeys
    : [],
  listingVideoIds: Array.isArray(product?.listingVideoIds)
    ? product.listingVideoIds
    : [],
  deliveryMethod: product?.deliveryMethod || "",
  deliveryDescription: product?.deliveryDescription || "",
  location: product?.location || "",
  locationDescription: product?.locationDescription || "",
  onlineMethod: product?.onlineMethod || "",
  methodDescription: product?.methodDescription || "",
  digitalImagePackKeys: Array.isArray(product?.digitalImagePackKeys)
    ? product.digitalImagePackKeys
    : [],
  digitalVideoId: product?.digitalVideoId || "",
  digitalPdfKey: product?.digitalPdfKey || "",
  hasCommissionOverride: Boolean(product?.commissionOverride),
  commissionPlatformPercent: product?.commissionOverride
    ? String(product.commissionOverride.platformPercent ?? "")
    : "",
  commissionPartnerPercent: product?.commissionOverride
    ? String(product.commissionOverride.partnerPercent ?? "")
    : "",
});

const resolvePreviewMap = (items = [], keyField, urlField) => {
  const map = new Map();
  items.forEach((item) => {
    const key = String(item?.[keyField] || "").trim();
    const url = String(item?.[urlField] || "").trim();
    if (key && url) map.set(key, url);
  });
  return map;
};

function EditableList({
  label,
  items,
  onChange,
  placeholder,
  getPreview,
  token,
  previewType = "image",
}) {
  const updateItem = (index, value) => {
    onChange(items.map((item, idx) => (idx === index ? value : item)));
  };

  const removeItem = (index) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const moveItem = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  };

  const addItem = () => {
    onChange([...items, ""]);
  };

  return (
    <div className={cls.mediaEditor}>
      <div className={cls.mediaHeader}>{label}</div>
      {!items.length ? <p className={cls.mediaEmpty}>No items yet.</p> : null}
      {items.map((item, index) => {
        const previewUrl = getPreview(item);
        const normalizedItem = String(item || "").trim();
        return (
          <div key={`${label}-${index}`} className={cls.mediaItem}>
            <div className={cls.mediaControls}>
              <input
                value={item}
                onChange={(event) => updateItem(index, event.target.value)}
                placeholder={placeholder}
              />
              <div className={cls.mediaButtons}>
                <button type="button" onClick={() => moveItem(index, -1)}>
                  ↑
                </button>
                <button type="button" onClick={() => moveItem(index, 1)}>
                  ↓
                </button>
                <button type="button" onClick={() => removeItem(index)}>
                  Remove
                </button>
              </div>
            </div>
            {previewUrl ? (
              previewType === "video" ? (
                <VideoPlayer
                  className={cls.mediaPreview}
                  token={token}
                  videoId={normalizedItem}
                  mode="proxy"
                  hlsMasterUrl={previewUrl}
                  controls
                  muted={false}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Media preview"
                  className={cls.mediaPreview}
                />
              )
            ) : (
              <p className={cls.mediaHint}>
                Preview unavailable for this value.
              </p>
            )}
          </div>
        );
      })}
      <button type="button" className={cls.addMediaButton} onClick={addItem}>
        Add item
      </button>
    </div>
  );
}

export default function AdminEditProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [listingImageItems, setListingImageItems] = useState([]);
  const [listingVideoItems, setListingVideoItems] = useState([]);
  const [uploadProgressPct, setUploadProgressPct] = useState(0);
  const [mediaPreviewMap, setMediaPreviewMap] = useState({
    listingImages: new Map(),
    listingVideos: new Map(),
    digitalImagePack: new Map(),
    digitalVideo: new Map(),
    digitalPdf: new Map(),
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAdminMarketplaceProduct(productId);
        if (!mounted) return;

        const product = data?.product || {};
        setForm(mapProductToForm(product));
        setListingImageItems(
          (product?.listingImageKeys || []).map((key, index) => ({
            id: makeMediaItemId("listing-image-existing"),
            source: "existing",
            key,
            previewUrl: product?.listingImages?.[index]?.url || "",
          })),
        );
        setListingVideoItems(
          (product?.listingVideoIds || []).map((videoId, index) => ({
            id: makeMediaItemId("listing-video-existing"),
            source: "existing",
            videoId,
            previewUrl: product?.listingVideos?.[index]?.url || "",
          })),
        );
        setMediaPreviewMap({
          listingImages: resolvePreviewMap(product.listingImages, "key", "url"),
          listingVideos: resolvePreviewMap(
            product.listingVideos,
            "videoId",
            "url",
          ),
          digitalImagePack: resolvePreviewMap(
            product.digitalImagePack,
            "key",
            "url",
          ),
          digitalVideo: resolvePreviewMap(
            product.digitalVideo ? [product.digitalVideo] : [],
            "videoId",
            "url",
          ),
          digitalPdf: resolvePreviewMap(
            product.digitalPdf ? [product.digitalPdf] : [],
            "key",
            "url",
          ),
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.msg || "Failed to load product.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [productId]);

  useEffect(
    () => () => {
      listingImageItems.forEach((item) => {
        if (item?.source === "new" && item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      listingVideoItems.forEach((item) => {
        if (item?.source === "new" && item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    [listingImageItems, listingVideoItems],
  );

  const selectedSubtype =
    form.category === PRODUCT_CATEGORIES.SERVICE
      ? form.serviceType
      : form.digitalType;

  const subtypeOptions = useMemo(() => {
    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
      return [PRODUCT_SUBTYPES.IN_PERSON, PRODUCT_SUBTYPES.ONLINE];
    }
    if (form.category === PRODUCT_CATEGORIES.DIGITAL) {
      return [
        PRODUCT_SUBTYPES.IMAGE_PACK,
        PRODUCT_SUBTYPES.VIDEO,
        PRODUCT_SUBTYPES.PDF,
      ];
    }
    return [];
  }, [form.category]);

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const onCategoryChange = (nextCategory) => {
    setForm((prev) => ({
      ...prev,
      category: nextCategory,
      serviceType:
        nextCategory === PRODUCT_CATEGORIES.SERVICE ? prev.serviceType : "",
      digitalType:
        nextCategory === PRODUCT_CATEGORIES.DIGITAL ? prev.digitalType : "",
    }));
  };

  const onSubtypeChange = (nextSubtype) => {
    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
      updateField("serviceType", nextSubtype);
      return;
    }
    if (form.category === PRODUCT_CATEGORIES.DIGITAL) {
      updateField("digitalType", nextSubtype);
    }
  };

  const moveItem = (setter, index, direction) => {
    setter((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onPct?.(pct);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const contentType = file?.type || "image/jpeg";
    const { data } = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType,
    });
    return data;
  };

  const presignVideoViaVideos = async (file) => {
    const contentType = file?.type || "application/octet-stream";
    const filename =
      String(file?.name || "") || `video.${extFromFile(file) || "mp4"}`;
    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data;
  };

  const uploadPendingMedia = async ({ onProgress } = {}) => {
    const newImageItems = listingImageItems.filter(
      (item) => item.source === "new",
    );
    const newVideoItems = listingVideoItems.filter(
      (item) => item.source === "new",
    );
    const totalUploads = newImageItems.length + newVideoItems.length;
    const progressByItem = {};
    const markProgress = (itemId, pct) => {
      progressByItem[itemId] = pct;
      if (!totalUploads) {
        onProgress?.(100);
        return;
      }
      const totalPct = Object.values(progressByItem).reduce(
        (sum, value) => sum + Number(value || 0),
        0,
      );
      onProgress?.(Math.round(totalPct / totalUploads));
    };

    const uploadedImageByItemId = {};
    const uploadedVideoByItemId = {};

    for (let i = 0; i < newImageItems.length; i += 1) {
      const item = newImageItems[i];
      const presign = await presignImagePoster(item.file);
      await putToS3WithProgress(presign.uploadUrl, item.file, (pct) =>
        markProgress(item.id, pct),
      );
      if (presign?.processedKey) {
        uploadedImageByItemId[item.id] = presign.processedKey;
      }
      markProgress(item.id, 100);
    }

    for (let i = 0; i < newVideoItems.length; i += 1) {
      const item = newVideoItems[i];
      const presign = await presignVideoViaVideos(item.file);
      const videoId = String(presign?.id || "").trim();
      if (!videoId) throw new Error("Server did not return a video id.");

      await putToS3WithProgress(
        presign.url,
        item.file,
        (pct) => markProgress(item.id, pct),
        presign.contentType || item?.file?.type,
      );
      await api.post(`/v1/videos/${videoId}/submit`);
      uploadedVideoByItemId[item.id] = videoId;
      markProgress(item.id, 100);
    }

    return { uploadedImageByItemId, uploadedVideoByItemId };
  };

  const buildPayload = (media = {}) => {
    const payload = {
      category: form.category,
      title: form.title.trim(),
      description: form.description.trim(),
      status: form.status,
      priceNoty: Number(form.priceNoty),
      listingImageKeys: listingImageItems
        .map((item) =>
          item.source === "existing"
            ? item.key
            : media.uploadedImageByItemId?.[item.id],
        )
        .filter(Boolean),
      listingVideoIds: listingVideoItems
        .map((item) =>
          item.source === "existing"
            ? item.videoId
            : media.uploadedVideoByItemId?.[item.id],
        )
        .filter(Boolean),
      deliveryMethod: form.deliveryMethod.trim() || null,
      deliveryDescription: form.deliveryDescription.trim() || null,
      location: form.location.trim() || null,
      locationDescription: form.locationDescription.trim() || null,
      onlineMethod: form.onlineMethod.trim() || null,
      methodDescription: form.methodDescription.trim() || null,
      digitalImagePackKeys: form.digitalImagePackKeys
        .map((item) => String(item || "").trim())
        .filter(Boolean),
      digitalVideoId: form.digitalVideoId.trim() || null,
      digitalPdfKey: form.digitalPdfKey.trim() || null,
      commissionOverride: form.hasCommissionOverride
        ? {
            platformPercent: Number(form.commissionPlatformPercent),
            partnerPercent: Number(form.commissionPartnerPercent),
          }
        : null,
    };

    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
      payload.serviceType = selectedSubtype || null;
      payload.digitalType = null;
    } else if (form.category === PRODUCT_CATEGORIES.DIGITAL) {
      payload.digitalType = selectedSubtype || null;
      payload.serviceType = null;
    } else {
      payload.serviceType = null;
      payload.digitalType = null;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setSubmitError("");
    setUploadProgressPct(0);

    try {
      if (listingImageItems.length > IMAGE_LIMIT) {
        throw new Error(`You can add up to ${IMAGE_LIMIT} listing images.`);
      }
      if (listingVideoItems.length > VIDEO_LIMIT) {
        throw new Error(`You can add up to ${VIDEO_LIMIT} listing videos.`);
      }

      const media = await uploadPendingMedia({
        onProgress: setUploadProgressPct,
      });
      await updateAdminMarketplaceProduct(productId, buildPayload(media));
      navigate(`/admin/marketplace/pending-products/${productId}`);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.msg || err?.message || "Failed to update product.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <section className={cls.page}>Loading product editor...</section>;
  }

  if (error) {
    return (
      <section className={cls.page}>
        <Link to={`/admin/marketplace/pending-products/${productId}`}>
          ← Back to product details
        </Link>
        <div className={cls.error}>{error}</div>
      </section>
    );
  }

  const isPhysical = form.category === PRODUCT_CATEGORIES.PHYSICAL;
  const isService = form.category === PRODUCT_CATEGORIES.SERVICE;
  const isInPerson =
    isService && selectedSubtype === PRODUCT_SUBTYPES.IN_PERSON;
  const isOnline = isService && selectedSubtype === PRODUCT_SUBTYPES.ONLINE;
  const isDigital = form.category === PRODUCT_CATEGORIES.DIGITAL;
  const isDigitalImagePack =
    isDigital && selectedSubtype === PRODUCT_SUBTYPES.IMAGE_PACK;
  const isDigitalVideo =
    isDigital && selectedSubtype === PRODUCT_SUBTYPES.VIDEO;
  const isDigitalPdf = isDigital && selectedSubtype === PRODUCT_SUBTYPES.PDF;

  return (
    <section className={cls.page}>
      <header className={cls.header}>
        <Link to={`/admin/marketplace/pending-products/${productId}`}>
          ← Back to product details
        </Link>
        <h1>Edit marketplace product</h1>
      </header>

      {submitError ? <div className={cls.error}>{submitError}</div> : null}

      <form className={cls.form} onSubmit={handleSubmit}>
        <label>
          Category
          <select
            value={form.category}
            onChange={(event) => onCategoryChange(event.target.value)}
          >
            {Object.values(PRODUCT_CATEGORIES).map((category) => (
              <option key={category} value={category}>
                {CATEGORY_LABELS[category] || category}
              </option>
            ))}
          </select>
        </label>

        {subtypeOptions.length ? (
          <label>
            Subtype
            <select
              value={selectedSubtype}
              onChange={(event) => onSubtypeChange(event.target.value)}
            >
              <option value="">Select subtype</option>
              {subtypeOptions.map((subtype) => (
                <option key={subtype} value={subtype}>
                  {SUBTYPE_LABELS[subtype] || subtype}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label>
          Listing status
          <select
            value={form.status}
            onChange={(event) => updateField("status", event.target.value)}
          >
            {Object.values(LISTING_STATUSES).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>

        <label>
          Title
          <input
            value={form.title}
            onChange={(event) => updateField("title", event.target.value)}
            maxLength={160}
            required
          />
        </label>

        <label>
          Description
          <textarea
            value={form.description}
            onChange={(event) => updateField("description", event.target.value)}
            rows={6}
            required
          />
        </label>

        <label>
          Price (Noty)
          <input
            type="number"
            min={1}
            step="0.01"
            value={form.priceNoty}
            onChange={(event) => updateField("priceNoty", event.target.value)}
            required
          />
        </label>

        <section className={cls.section}>
          <h2>Commission override</h2>
          <label className={cls.checkboxRow}>
            <input
              type="checkbox"
              checked={form.hasCommissionOverride}
              onChange={(event) =>
                updateField("hasCommissionOverride", event.target.checked)
              }
            />
            Use product-specific commission override
          </label>

          {form.hasCommissionOverride ? (
            <div className={cls.twoCol}>
              <label>
                Platform %
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.commissionPlatformPercent}
                  onChange={(event) =>
                    updateField("commissionPlatformPercent", event.target.value)
                  }
                  required={form.hasCommissionOverride}
                />
              </label>
              <label>
                Partner %
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.commissionPartnerPercent}
                  onChange={(event) =>
                    updateField("commissionPartnerPercent", event.target.value)
                  }
                  required={form.hasCommissionOverride}
                />
              </label>
            </div>
          ) : null}
        </section>

        <section className={cls.mediaEditor}>
          <div className={cls.mediaHeader}>
            Listing images (max {IMAGE_LIMIT}) and videos (max {VIDEO_LIMIT})
          </div>

          <label>
            Upload images
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => {
                const picked = Array.from(event.target.files || []);
                if (!picked.length) return;
                const remaining = Math.max(
                  0,
                  IMAGE_LIMIT - listingImageItems.length,
                );
                if (remaining <= 0) {
                  setSubmitError(
                    `You can add up to ${IMAGE_LIMIT} listing images.`,
                  );
                  event.target.value = "";
                  return;
                }
                const accepted = picked.slice(0, remaining);
                setListingImageItems((prev) => [
                  ...prev,
                  ...accepted.map((file) => ({
                    id: makeMediaItemId("listing-image-new"),
                    source: "new",
                    file,
                    previewUrl: URL.createObjectURL(file),
                  })),
                ]);
                if (picked.length > accepted.length) {
                  setSubmitError(
                    `Only ${remaining} more image(s) can be added for this listing.`,
                  );
                }
                event.target.value = "";
              }}
            />
          </label>

          <div className={cls.mediaGrid}>
            {listingImageItems.length ? (
              listingImageItems.map((item, index) => (
                <article key={item.id} className={cls.mediaCard}>
                  <img
                    className={cls.mediaPreview}
                    src={
                      item.previewUrl ||
                      mediaPreviewMap.listingImages.get(
                        String(item.key || "").trim(),
                      )
                    }
                    alt={`Listing image ${index + 1}`}
                  />
                  <p className={cls.mediaHint}>
                    #{index + 1}{" "}
                    {item.source === "new"
                      ? item?.file?.name || "New image"
                      : item?.key}
                  </p>
                  <div className={cls.mediaButtons}>
                    <button
                      type="button"
                      onClick={() => moveItem(setListingImageItems, index, -1)}
                      disabled={index === 0}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(setListingImageItems, index, 1)}
                      disabled={index >= listingImageItems.length - 1}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setListingImageItems((prev) => {
                          const removed = prev[index];
                          if (removed?.source === "new" && removed.previewUrl) {
                            URL.revokeObjectURL(removed.previewUrl);
                          }
                          return prev.filter((_, i) => i !== index);
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <p className={cls.mediaEmpty}>No listing images yet.</p>
            )}
          </div>

          <label>
            Upload videos
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={(event) => {
                const picked = Array.from(event.target.files || []);
                if (!picked.length) return;
                const remaining = Math.max(
                  0,
                  VIDEO_LIMIT - listingVideoItems.length,
                );
                if (remaining <= 0) {
                  setSubmitError(
                    `You can add up to ${VIDEO_LIMIT} listing videos.`,
                  );
                  event.target.value = "";
                  return;
                }
                const accepted = picked.slice(0, remaining);
                setListingVideoItems((prev) => [
                  ...prev,
                  ...accepted.map((file) => ({
                    id: makeMediaItemId("listing-video-new"),
                    source: "new",
                    file,
                    previewUrl: URL.createObjectURL(file),
                  })),
                ]);
                if (picked.length > accepted.length) {
                  setSubmitError(
                    `Only ${remaining} more video(s) can be added for this listing.`,
                  );
                }
                event.target.value = "";
              }}
            />
          </label>

          <div className={cls.mediaGrid}>
            {listingVideoItems.length ? (
              listingVideoItems.map((item, index) => (
                <article key={item.id} className={cls.mediaCard}>
                  {item.source === "new" ? (
                    <video
                      className={cls.mediaPreview}
                      src={item.previewUrl}
                      controls
                    />
                  ) : (
                    <VideoPlayer
                      className={cls.mediaPreview}
                      token={token}
                      videoId={String(item.videoId || "").trim()}
                      mode="proxy"
                      hlsMasterUrl={item.previewUrl}
                      controls
                      muted={false}
                    />
                  )}
                  <p className={cls.mediaHint}>
                    #{index + 1}{" "}
                    {item.source === "new"
                      ? item?.file?.name || "New video"
                      : item?.videoId}
                  </p>
                  <div className={cls.mediaButtons}>
                    <button
                      type="button"
                      onClick={() => moveItem(setListingVideoItems, index, -1)}
                      disabled={index === 0}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(setListingVideoItems, index, 1)}
                      disabled={index >= listingVideoItems.length - 1}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setListingVideoItems((prev) => {
                          const removed = prev[index];
                          if (removed?.source === "new" && removed.previewUrl) {
                            URL.revokeObjectURL(removed.previewUrl);
                          }
                          return prev.filter((_, i) => i !== index);
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <p className={cls.mediaEmpty}>No listing videos yet.</p>
            )}
          </div>
        </section>

        {isPhysical ? (
          <>
            <label>
              Delivery method (physical)
              <input
                value={form.deliveryMethod}
                onChange={(event) =>
                  updateField("deliveryMethod", event.target.value)
                }
              />
            </label>

            <label>
              Delivery description (physical)
              <textarea
                value={form.deliveryDescription}
                onChange={(event) =>
                  updateField("deliveryDescription", event.target.value)
                }
                rows={3}
              />
            </label>
          </>
        ) : null}

        {isInPerson ? (
          <>
            <label>
              Service location (in person)
              <input
                value={form.location}
                onChange={(event) =>
                  updateField("location", event.target.value)
                }
              />
            </label>

            <label>
              Service location description (in person)
              <textarea
                value={form.locationDescription}
                onChange={(event) =>
                  updateField("locationDescription", event.target.value)
                }
                rows={3}
              />
            </label>
          </>
        ) : null}

        {isOnline ? (
          <>
            <label>
              Online method (service)
              <input
                value={form.onlineMethod}
                onChange={(event) =>
                  updateField("onlineMethod", event.target.value)
                }
              />
            </label>

            <label>
              Online method description (service)
              <textarea
                value={form.methodDescription}
                onChange={(event) =>
                  updateField("methodDescription", event.target.value)
                }
                rows={3}
              />
            </label>
          </>
        ) : null}

        {isDigitalImagePack ? (
          <EditableList
            label="Digital image pack keys"
            items={form.digitalImagePackKeys}
            onChange={(next) => updateField("digitalImagePackKeys", next)}
            placeholder="processed/public/.../image.jpg"
            token={token}
            getPreview={(key) =>
              mediaPreviewMap.digitalImagePack.get(String(key || "").trim())
            }
          />
        ) : null}

        {isDigitalVideo ? (
          <div className={cls.mediaEditor}>
            <label>
              Digital video ID
              <input
                value={form.digitalVideoId}
                onChange={(event) =>
                  updateField("digitalVideoId", event.target.value)
                }
              />
            </label>
            {mediaPreviewMap.digitalVideo.get(form.digitalVideoId.trim()) ? (
              <VideoPlayer
                className={cls.mediaPreview}
                token={token}
                videoId={form.digitalVideoId.trim()}
                mode="proxy"
                hlsMasterUrl={mediaPreviewMap.digitalVideo.get(
                  form.digitalVideoId.trim(),
                )}
                controls
                muted={false}
              />
            ) : (
              <p className={cls.mediaHint}>
                Preview unavailable for this value.
              </p>
            )}
          </div>
        ) : null}

        {isDigitalPdf ? (
          <div className={cls.mediaEditor}>
            <label>
              Digital PDF key
              <input
                value={form.digitalPdfKey}
                onChange={(event) =>
                  updateField("digitalPdfKey", event.target.value)
                }
              />
            </label>
            {mediaPreviewMap.digitalPdf.get(form.digitalPdfKey.trim()) ? (
              <a
                href={mediaPreviewMap.digitalPdf.get(form.digitalPdfKey.trim())}
                target="_blank"
                rel="noreferrer"
                className={cls.pdfLink}
              >
                Open current PDF preview
              </a>
            ) : (
              <p className={cls.mediaHint}>
                Preview unavailable for this value.
              </p>
            )}
          </div>
        ) : null}

        {saving ? (
          <p className={cls.mediaHint}>
            Uploading media... {uploadProgressPct}%
          </p>
        ) : null}

        <button type="submit" disabled={saving}>
          {saving ? "Saving..." : "Update product"}
        </button>
      </form>
    </section>
  );
}
